import { Button, Center, Group, Loader, Text } from "@mantine/core";
import { useHotkeys } from "@mantine/hooks";
import { IconArrowLeft } from "@tabler/icons-react";
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, useRef, useState, MouseEvent } from "react";
import { useBugScreenshot } from "../../api/tickets/useBugScreenshot";
import { requireAdminRole } from "../../helpers/role.helpers";

export const Route = createFileRoute(
  "/(fullscreen)/admin/tickets/$id/screenshot/$bugId"
)({
  // This route sits outside the (app)/admin layout, so it needs its own guard.
  beforeLoad: requireAdminRole,
  component: RouteComponent,
});

function RouteComponent() {
  const { id, bugId } = Route.useParams();
  const { data } = useBugScreenshot(bugId);
  const [zoomed, setZoomed] = useState(false);
  const [clickPosition, setClickPosition] = useState<{
    percentX: number;
    percentY: number;
    viewportX: number;
    viewportY: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const img = `data:image/jpeg;base64,${data}`;
  const navigate = useNavigate();

  const canGoBack = useCanGoBack();
  const router = useRouter();

  const handleGoBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      navigate({
        to: "/admin/tickets/$id",
        replace: true,
        params: { id },
      });
    }
  };

  const handleImageClick = (e: MouseEvent<HTMLImageElement>) => {
    if (!imgRef.current || !containerRef.current) return;

    const imgRect = imgRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const percentX = (e.clientX - imgRect.left) / imgRect.width;
    const percentY = (e.clientY - imgRect.top) / imgRect.height;

    const viewportX = e.clientX - containerRect.left;
    const viewportY = e.clientY - containerRect.top;

    setClickPosition({ percentX, percentY, viewportX, viewportY });
    setZoomed((z) => !z);
  };

  useEffect(() => {
    if (zoomed && clickPosition && containerRef.current && imgRef.current) {
      requestAnimationFrame(() => {
        if (!containerRef.current || !imgRef.current) return;

        const container = containerRef.current;
        const img = imgRef.current;
        const imgRect = img.getBoundingClientRect();

        const targetX = clickPosition.percentX * imgRect.width;
        const targetY = clickPosition.percentY * imgRect.height;

        container.scrollLeft = targetX - clickPosition.viewportX;
        container.scrollTop = targetY - clickPosition.viewportY;
      });
    }
  }, [zoomed, clickPosition]);

  useHotkeys([["escape", handleGoBack]]);

  return (
    <div
      ref={containerRef}
      style={{
        display: zoomed ? undefined : "flex",
        alignItems: zoomed ? undefined : "center",
        backgroundColor: "var(--mantine-color-bluegray-8)",
        overflow: "scroll",
        width: "100vw",
        height: "100vh",
      }}
    >
      {data ? (
        <img
          ref={imgRef}
          src={img}
          width={zoomed ? "auto" : "100%"}
          style={{ cursor: zoomed ? "zoom-out" : "zoom-in" }}
          onClick={handleImageClick}
        />
      ) : (
        <Center w="100%">
          <Loader size="md" />
        </Center>
      )}
      <Group pos="fixed" top={40} left={40}>
        <Button
          leftSection={<IconArrowLeft size={16} />}
          radius="xl"
          onClick={handleGoBack}
        >
          Go back
        </Button>
        <Text fw="bold" c="white">
          Screenshot ticket {id}
        </Text>
      </Group>
    </div>
  );
}
